import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "@/graphql";
import {
  GET_SCENARIO_CARDS,
  GET_SCENARIO,
  CALCULATE_SCENARIO_METRICS,
  CREATE_SCENARIO_CARD,
  UPDATE_SCENARIO,
  DELETE_SCENARIO,
  GET_COUNTRY_SUMMARY,
  GET_HTS_CODES,
} from "@/graphql/dashboards/scenariomodeling";

export interface DateRange {
  start: string;
  end: string;
}

export interface ScenarioCard {
  _id: string;
  name: string;
  country_list: string[];
  rate: number;
  hts_codes: string[];
  clientId: string;
  tenantId: string;
  dateRange: DateRange;
}

export interface ScenarioMetrics {
  dutyAmount: number;
  firstSale: number;
  exclusion: number;
  ftz: number;
  engineering: number;
  reclassification: number;
  valueUnbundling: number;
}

export interface PaginationMeta {
  countDocs: number;
  pageNo: number;
  pageSize: number;
}

export interface ScenarioCardInput {
  name: string;
  country_list: string[];
  rate: number;
  hts_codes: string[];
  clientId: string;
  tenantId?: string;
  dateRange: DateRange;
}

export interface UpdateScenarioCardInput {
  name?: string;
  country_list?: string[];
  rate?: number;
  hts_codes?: string[];
  dateRange?: DateRange;
}

export interface GetScenarioCardsResponse {
  scenarioCards: {
    data: ScenarioCard[];
    meta: PaginationMeta;
  };
}

export interface GetScenarioResponse {
  getScenario: {
    data: ScenarioCard;
  };
}

export interface CreateScenarioResponse {
  createScenarioCard: {
    data: string;
  };
}

export interface CalculateMetricsResponse {
  calculateScenarioMetrics: ScenarioMetrics;
}

export interface CountrySummary {
  country_code: string;
  total_duties: number;
  total_goods_value: number;
}

export interface HTSCodeSummary {
  hts_code: string;
  duty_value: number;
  goods_value: number;
}

export interface GetCountrySummaryResponse {
  getCountryOriginSummary: CountrySummary[];
}

export interface GetHTSCodesResponse {
  getHTSCodesByCountry: HTSCodeSummary[];
}

export const scenarioModelingApi = createApi({
  reducerPath: "api/scenarioModeling",
  tagTypes: ["ScenarioModeling"],
  baseQuery,
  endpoints: (builder) => ({
    getScenarioCards: builder.query<GetScenarioCardsResponse, { clientId: string; pageNo?: number; pageSize?: number }>({
      query: (variables) => ({
        document: GET_SCENARIO_CARDS,
        variables,
      }),
      providesTags: ["ScenarioModeling"],
    }),

    getSingleScenario: builder.query<GetScenarioResponse, { id: string }>({
      query: (variables) => ({
        document: GET_SCENARIO,
        variables,
      }),
      providesTags: ["ScenarioModeling"],
    }),

    calculateMetrics: builder.query<CalculateMetricsResponse, { scenarioId: string }>({
      query: (variables) => ({
        document: CALCULATE_SCENARIO_METRICS,
        variables,
      }),
    }),

    createScenarioCard: builder.mutation<CreateScenarioResponse, { scenarioCard: ScenarioCardInput }>({
      query: (variables) => ({
        document: CREATE_SCENARIO_CARD,
        variables,
      }),
      invalidatesTags: ["ScenarioModeling"],
    }),

    updateScenario: builder.mutation<boolean, { id: string; input: UpdateScenarioCardInput }>({
      query: (variables) => ({
        document: UPDATE_SCENARIO,
        variables,
      }),
      invalidatesTags: ["ScenarioModeling"],
    }),

    deleteScenario: builder.mutation<boolean, { id: string }>({
      query: (variables) => ({
        document: DELETE_SCENARIO,
        variables,
      }),
      invalidatesTags: ["ScenarioModeling"],
    }),

    getCountrySummary: builder.query<GetCountrySummaryResponse, { clientId: string; dateFrom?: string; dateTo?: string }>({
      query: (variables) => ({
        document: GET_COUNTRY_SUMMARY,
        variables,
      }),
    }),

    getHTSCodes: builder.query<GetHTSCodesResponse, { clientId: string; country_codes?: string[]; dateFrom?: string; dateTo?: string }>({
      query: (variables) => ({
        document: GET_HTS_CODES,
        variables,
      }),
    }),
  }),
});

export const {
  useGetScenarioCardsQuery,
  useGetSingleScenarioQuery,
  useCalculateMetricsQuery,
  useCreateScenarioCardMutation,
  useUpdateScenarioMutation,
  useDeleteScenarioMutation,
  useGetCountrySummaryQuery,
  useGetHTSCodesQuery,
} = scenarioModelingApi;


